import { Box, Text, Flex } from '@chakra-ui/react';
import BigNumber from 'bignumber.js';
import React from 'react';

import type { Transaction } from 'types/api/transaction';

import { route } from 'nextjs-routes';

import config from 'configs/app';
import getValueWithUnit from 'lib/getValueWithUnit';
import { useI18n } from 'lib/i18n/context';
import { currencyUnits } from 'lib/units';
import BlobEntity from 'ui/shared/entities/blob/BlobEntity';
import IconSvg from 'ui/shared/IconSvg';
import LinkInternal from 'ui/shared/links/LinkInternal';
import TextSeparator from 'ui/shared/TextSeparator';
import TxFee from 'ui/shared/tx/TxFee';

const TxAdditionalInfoContent = ({ tx }: { tx: Transaction }) => {
  const { t } = useI18n();
  const sectionProps = {
    marginTop: '12px',
  };

  const sectionTitleProps = {
    color: '#8B959F',
    fontSize: '12px',
  };

  return (
    <>
      <Flex justifyContent="space-between" alignItems="center">
        <Text sx={{
          fontSize: '12px',
          color: '#8B959F',
        }}>{ t('txs.additionalInfo') }</Text>
        <LinkInternal href={ route({ pathname: '/tx/[hash]', query: { hash: tx.hash } }) } sx={{
          fontSize: '12px',
          fontWeight: 500,
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
        }}>
          { t('txs.moreDetails') }
          <IconSvg name="arrows/arrow-right-green" boxSize="12px"/>
        </LinkInternal>
      </Flex>
      { tx.gas_used !== null && (
        <Box { ...sectionProps } >
          <Text { ...sectionTitleProps }>{ t('txs.gasLimitAndUsageByTransaction') }</Text>
          <Flex fontSize="16px" fontWeight="500">
            <Text>{ BigNumber(tx.gas_used).toFormat() }</Text>
            <TextSeparator/>
            <Text>{ BigNumber(tx.gas_limit).toFormat() }</Text>
            { /* <Utilization ml={ 4 } value={ Number(BigNumber(tx.gas_used).dividedBy(BigNumber(tx.gas_limit)).toFixed(2)) }/> */ }
          </Flex>
        </Box>
      ) }
      <Flex gap="12px">
        { !config.UI.views.tx.hiddenFields?.tx_fee && (
          <Box { ...sectionProps }>
            { (tx.stability_fee !== undefined || tx.fee.value !== null) && (
              <>
                <Text { ...sectionTitleProps }>{ t('txs.transactionFee') }</Text>
                <TxFee tx={ tx } withUsd accuracyUsd={ 2 } rowGap={ 0 }/>
              </>
            ) }
          </Box>
        ) }
        { !config.UI.views.tx.hiddenFields?.gas_fees &&
        (tx.base_fee_per_gas !== null || tx.max_fee_per_gas !== null || tx.max_priority_fee_per_gas !== null) && (
          <Box { ...sectionProps }>
            <Text { ...sectionTitleProps }>{ t('txs.gasFeesGwei', { unit: currencyUnits.gwei }) }</Text>
            { tx.base_fee_per_gas !== null && (
              <Box>
                <Text as="span" fontWeight="500">{ t('txs.base') }: </Text>
                <Text fontWeight="700" as="span">{ getValueWithUnit(tx.base_fee_per_gas, 'gwei').toFormat() }</Text>
              </Box>
            ) }
            { tx.max_fee_per_gas !== null && (
              <Box mt={ 1 }>
                <Text as="span" fontWeight="500">{ t('txs.max') }: </Text>
                <Text fontWeight="700" as="span">{ getValueWithUnit(tx.max_fee_per_gas, 'gwei').toFormat() }</Text>
              </Box>
            ) }
            { tx.max_priority_fee_per_gas !== null && (
              <Box mt={ 1 }>
                <Text as="span" fontWeight="500">{ t('txs.maxPriority') }: </Text>
                <Text fontWeight="700" as="span">{ getValueWithUnit(tx.max_priority_fee_per_gas, 'gwei').toFormat() }</Text>
              </Box>
            ) }
          </Box>
        ) }
      </Flex>
      { tx.blob_versioned_hashes && tx.blob_versioned_hashes.length > 0 && (
        <Box { ...sectionProps } mb={ 4 }>
          <Flex alignItems="center" justifyContent="space-between">
            <Text { ...sectionTitleProps }>{ t('txs.blobsCount', { count: tx.blob_versioned_hashes.length }) }</Text>
            { tx.blob_versioned_hashes.length > 3 && (
              <LinkInternal
                href={ route({ pathname: '/tx/[hash]', query: { hash: tx.hash, tab: 'blobs' } }) }
                mb={ 3 }
              >
                { t('txs.viewAll') }
              </LinkInternal>
            ) }
          </Flex>
          <Flex flexDir="column" rowGap={ 3 }>
            { tx.blob_versioned_hashes.slice(0, 3).map((hash, index) => (
              <Flex key={ hash } columnGap={ 2 }>
                <Box fontWeight={ 500 }>{ index + 1 }</Box>
                <BlobEntity hash={ hash } noIcon/>
              </Flex>
            )) }
          </Flex>
        </Box>
      ) }

      { !(tx.blob_versioned_hashes && tx.blob_versioned_hashes.length > 0) && (
        <Flex { ...sectionProps } gap="12px">
          <Box>
            <Text as="span" fontSize="12px" color="#8B959F">{ t('txs.txnType') }: </Text>
            <Text fontWeight="600" as="span">{ tx.type }</Text>
            { tx.type === 2 && <Text fontWeight="400" as="span" ml={ 1 } color="gray.500">(EIP-1559)</Text> }
          </Box>
          <Box>
            <Text as="span" fontSize="12px" color="#8B959F">{ t('txs.nonce') }: </Text>
            <Text fontWeight="600" as="span">{ tx.nonce }</Text>
          </Box>
          <Box>
            <Text as="span" fontSize="12px" color="#8B959F">{ t('txs.position') }: </Text>
            <Text fontWeight="600" as="span">{ tx.position }</Text>
          </Box>
        </Flex>
      ) }
    </>
  );
};

export default React.memo(TxAdditionalInfoContent);
